// Small facts about designs that BOTH the browser and the server need.
//
// Kept free of node-only imports — lib/commitment_compositor.js pulls in sharp
// and cannot be imported from a client component.

// A LAYERED background's layer-2 band: 12in wide by definition, with a height
// the user chooses inside this range with the crop tool.
//
// One definition, because there are five places that have to agree — the
// background page's cropper, the editor pop-up's cropper, /api/backgrounds/
// upload, /api/backgrounds/recrop, and the compositor's own clamp. They were
// five independent copies of the same two numbers, and every layered bug so
// far has been two of those copies disagreeing.
export const BOTTOM_MIN_IN = 5.5;
export const BOTTOM_MAX_IN = 7.5;
export const BAND_W_IN = 12;
// Widest (shortest band) .. narrowest (tallest band), as width/height.
export const BOTTOM_ASPECT_RANGE = {
  min: BAND_W_IN / BOTTOM_MAX_IN,
  max: BAND_W_IN / BOTTOM_MIN_IN,
};

// Empty: no design forces a second background choice during setup. A layered
// background is a choice made in the background flow rather than a property of
// any template.
export const DESIGNS_NEEDING_TOP_BG = new Set();

export function designNeedsTopBackground(design) {
  return DESIGNS_NEEDING_TOP_BG.has(String(design || '').toLowerCase());
}

// Empty: no design's background is split by the template. Single is one 4:5
// image and Layered is a sky plus a band, for every template alike.
export const SPLIT_BACKGROUND_DESIGNS = new Set();

export function designHasSplitBackground(design) {
  return SPLIT_BACKGROUND_DESIGNS.has(String(design || '').toLowerCase());
}

// Split designs' BOTTOM (user-chosen) background plate height, in canvas px
// at 300dpi (canvas is 3600x4500, i.e. 12in wide either way): 12in x ~5.59in.
// Compact and Condensed are separate designs with their own placement diagrams,
// so each has its own entry even though the two diagrams currently agree.
// Nothing sends `half` any more (the Single / Layered choice replaced it), so
// this only backs the unused half-canvas branch of the crop tools.
//
// Kept in sync manually with COMPACT_ZONES / CONDENSED_ZONES in
// lib/commitment_compositor.js, which pulls in sharp and can't be imported
// from a client component: CH(4500) - 2822 = 1678.
export const SPLIT_BOTTOM_H_PX = { compact: 1678, condensed: 1678 };

export function splitBottomHeightPx(design) {
  const key = String(design || '').toLowerCase();
  // An unknown design falls back to Compact's plate rather than returning
  // undefined, which would propagate into crop aspect and floor maths as NaN
  // instead of failing anywhere visible.
  return SPLIT_BOTTOM_H_PX[key] || SPLIT_BOTTOM_H_PX.compact;
}

// Split designs' TOP plate height. Empty now that Skyline is retired — it was
// the only design with a user-picked top plate. A layered background's sky is
// the full canvas instead, which is what the fallback below returns.
export const SPLIT_TOP_H_PX = {};

export function splitTopHeightPx(design) {
  const key = String(design || '').toLowerCase();
  // No design has a user-picked TOP plate any more (Skyline was the only one).
  // A layered sky is the FULL canvas, so callers asking for a top-half height
  // get the canvas height rather than undefined.
  return SPLIT_TOP_H_PX[key] || 4500;
}

// Display name + one-line description for every design, shown as the header
// and footer on each template thumbnail on the template selection page.
//
// Descriptions say what the LAYOUT does — what the design puts where — since
// the thumbnail already shows the styling. Kept short enough for one line
// under a tile.
export const DESIGN_INFO = {
  // Announcement + shared
  classic:   { name: 'Classic',   description: 'Name and school stacked over a full-bleed background.' },
  identity:  { name: 'Identity',  description: 'Large name with the school mark set alongside.' },
  bold:      { name: 'Bold',      description: 'Oversized name type dominating the frame.' },
  split:     { name: 'Split',     description: 'Name and details divided across a banded layout.' },
  signature: { name: 'Signature', description: 'Script-style name across the lower third.' },
  // Compact (announcement) and Condensed (print) were one design, `night`. They
  // are now two: each has its own placement diagram, zones, brand logo zone and
  // key, so they can diverge without touching each other.
  compact:   { name: 'Compact',   description: 'Tighter layout with the name block low in the frame.' },
  // Print / composite
  condensed: { name: 'Condensed', description: 'Tighter layout with the name block low in the frame.' },
  complete:  { name: 'Complete',  description: 'Full layout — name, number, and school all in frame.' },
  cinematic: { name: 'Complete',  description: 'Full layout — name, number, and school all in frame.' },
  smoke:     { name: 'Smoke',     description: 'Number behind the player with a smoke sweep across.' },
  jersey:    { name: 'Jersey',    description: 'Jersey-style initials with the number front and centre.' },
  title:     { name: 'Title',     description: 'A single headline title above the player.' },
  number:    { name: 'Number',    description: 'The number as the hero, player set over it.' },
  name:      { name: 'Name',      description: 'Last name across the base in heavy display type.' },
  offset:    { name: 'Offset',    description: 'Player set off-centre with the details beside them.' },
};

// There is no hidden-design SET any more. Which designs are offered is now
// stated positively, as TEMPLATE_ORDER in app/templates/page.jsx: a design
// absent from its flow's list is not shown. Hiding by subtraction needed a
// second list to stay in sync with the first, and the editor's Change
// Template pop-up — the other place a design could be picked — read neither.

// Every design the pickers offer ships its own preview art in the repo:
//   public/template-thumbs/<announcement|composite>/<design>.png
// generated from the full-resolution renders in templates/thumbnails/<flow>/.
// The list IS the picker's catalog: TEMPLATE_ORDER in app/templates/page.jsx
// must name the same designs, and each has a placement diagram under
// trainingimages/templates/<flow>/<design>/.
const LOCAL_THUMB_DESIGNS = {
  announcement: ['classic', 'identity', 'bold', 'split', 'signature', 'compact'],
  composite: ['complete', 'cinematic', 'condensed', 'smoke', 'jersey', 'title', 'number', 'name', 'offset'],
};

/**
 * @param {string} design
 * @param {string} flow  'announcement' or a 'print…' flow — the two catalogs
 *   are separate, and so are their thumbnails and their designs.
 * @returns {string|null} the local thumbnail, or null when there is none.
 */
export function localThumbUrl(design, flow) {
  const key = String(design || '').toLowerCase();
  const folder = String(flow || '').startsWith('print') ? 'composite' : 'announcement';
  if (!LOCAL_THUMB_DESIGNS[folder].includes(key)) return null;
  // "cinematic" and "complete" are the same design under two keys; only one
  // image file exists, under the catalog's own name for it.
  return `/template-thumbs/${folder}/${key === 'cinematic' ? 'complete' : key}.png`;
}

// Never throws and never returns undefined fields — an unknown design (a new
// template, a typo) falls back to a title-cased key and no description, so a
// tile still renders a sensible header rather than "undefined".
export function designInfo(design) {
  const key = String(design || '').toLowerCase();
  const hit = DESIGN_INFO[key];
  if (hit) return hit;
  return {
    name: key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Template',
    description: '',
  };
}
